
import React from "react";
import { CellPosition, ColumnKey, Day } from "../types/workout-types";
import { TableHeader } from "./table/TableHeader";
import { ExerciseRow } from "./table/ExerciseRow";
import { WorkoutNotes } from "./table/WorkoutNotes";
import { EmptyWorkout } from "./table/EmptyWorkout";
import { useCellTransition } from "../hooks/useCellTransition";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";

interface ExerciseTableProps {
  day: Day;
  activeCell: CellPosition | null;
  editMode: boolean;
  tempValue: string;
  currentDayIndex: number;
  columns: ColumnKey[];
  setHasEdited: (hasEdited: boolean) => void;
  finishEditingWithSave: (id: string, field: ColumnKey, tempValue: string) => void;
  setActiveCell: (cell: CellPosition | null) => void;
  startEditing: (rowIndex: number, colIndex: number, value: string) => void;
  handleCellClick: (rowIndex: number, colIndex: number, value: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => void;
  inputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  handleNotesChange: (notes: string) => void;
  setForceRender?: (value: number | ((prevValue: number) => number)) => void;
  dayIndex?: number;
  setTempValue: (value: string) => void;
}

function ExerciseTableComponent({
  day,
  activeCell,
  editMode,
  tempValue,
  currentDayIndex,
  columns,
  setHasEdited,
  finishEditingWithSave,
  setActiveCell,
  startEditing,
  handleCellClick,
  handleKeyDown,
  inputRefs,
  handleNotesChange,
  setForceRender,
  dayIndex,
  setTempValue,
}: ExerciseTableProps) {
  // Enable performance monitoring in development
  usePerformanceMonitor('ExerciseTable');
  
  // Use the custom hook for cell transitions
  useCellTransition(activeCell, editMode, inputRefs);

  const handleClickOutside = React.useCallback(() => {
    if (editMode && activeCell) {
      const exercise = day.exercises[activeCell.rowIndex];
      const field = columns[activeCell.colIndex];
      finishEditingWithSave(exercise.id, field, tempValue);
    }
    setActiveCell(null);
    setHasEdited(false);
  }, [editMode, activeCell, day.exercises, columns, finishEditingWithSave, tempValue, setActiveCell, setHasEdited]);

  // If there are no exercises, show a friendly message
  if (!day.exercises || day.exercises.length === 0) {
    return <EmptyWorkout />;
  }

  return (
    <>
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse table-auto border border-white/10 rounded-lg">
          <TableHeader />
          <tbody>
            {day.exercises.map((exercise, rowIndex) => (
              <ExerciseRow
                key={`${exercise.id}-${rowIndex}`}
                exercise={exercise}
                rowIndex={rowIndex}
                columns={columns}
                activeCell={activeCell}
                editMode={editMode}
                tempValue={tempValue}
                currentDayIndex={currentDayIndex}
                dayIndex={dayIndex}
                handleCellClick={handleCellClick}
                startEditing={startEditing}
                setActiveCell={setActiveCell}
                setHasEdited={setHasEdited}
                finishEditingWithSave={finishEditingWithSave}
                handleKeyDown={handleKeyDown}
                inputRefs={inputRefs}
                setTempValue={setTempValue}
              />
            ))}
          </tbody>
        </table>
      </div>

      <WorkoutNotes 
        notes={day.notes || ""} 
        handleNotesChange={handleNotesChange} 
      />
    </>
  );
}

// Apply memoization with custom comparison
export const ExerciseTable = React.memo(ExerciseTableComponent, (prevProps, nextProps) => {
  // Only re-render if these props change:
  // 1. day.exercises length changes
  // 2. activeCell changes
  // 3. editMode changes
  // 4. tempValue changes when in edit mode
  
  const prevDay = prevProps.day;
  const nextDay = nextProps.day;
  
  // Quick length check
  if ((prevDay.exercises?.length || 0) !== (nextDay.exercises?.length || 0)) {
    return false; // Re-render if length changes
  }
  
  // Compare active cell and edit states
  const activeStateChanged = 
    JSON.stringify(prevProps.activeCell) !== JSON.stringify(nextProps.activeCell) ||
    prevProps.editMode !== nextProps.editMode;
    
  if (activeStateChanged) {
    return false; // Re-render if active state changes
  }
  
  // If in edit mode, compare temp value
  if (prevProps.editMode && prevProps.tempValue !== nextProps.tempValue) {
    return false; // Re-render if temp value changes while editing
  }
  
  // For deep comparison of exercises, just check IDs and simple props
  // This avoids deep comparison of entire objects which can be expensive
  if (prevDay.exercises && nextDay.exercises) {
    for (let i = 0; i < prevDay.exercises.length; i++) {
      if (
        prevDay.exercises[i].id !== nextDay.exercises[i].id ||
        prevDay.exercises[i].name !== nextDay.exercises[i].name ||
        prevDay.exercises[i].sets !== nextDay.exercises[i].sets ||
        prevDay.exercises[i].reps !== nextDay.exercises[i].reps
      ) {
        return false; // Re-render if any critical exercise data changes
      }
    }
  }
  
  // If day notes differ, re-render
  if (prevDay.notes !== nextDay.notes) {
    return false;
  }
  
  return true; // No relevant changes, skip render
});
